/**
 * ControlDetailSheet — Full detail view for a single compliance Control.
 *
 * Shows everything an assessor or implementer needs:
 *  - Identity: name, description, framework mappings
 *  - Status: passing/failing/partial + 30-day pass rate history
 *  - Ownership: owner, re-assignment flow
 *  - Automation: automated/manual/hybrid, ArgusAI primitive backing it
 *  - Test procedure: expected result, last result, test frequency
 *  - Evidence: linked evidence records
 *  - Activity: recent test runs
 *
 * Actions:
 *  - Run test now
 *  - Reassign owner
 *  - Mark status (passing / failing / needs review)
 *  - Link evidence
 */

import { useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import {
  X, Shield, User, Clock, CheckCircle2, XCircle, AlertTriangle,
  RefreshCw, Zap, FileText, ArrowRight, Calendar, Copy, ExternalLink,
  Target, Bot, PlayCircle, History, Cpu,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { Control, Evidence, FrameworkId } from '@/lib/mock-trust-data';
import {
  CONTROL_STATUS_STYLES,
} from '@/lib/mock-trust-data';
import { useTrustEvidence } from '@/lib/use-trust-evidence';
import { useTrustEntities } from '@/lib/use-trust-entities';

type DetailTab = 'overview' | 'test' | 'evidence' | 'history';

interface ControlDetailSheetProps {
  control: Control | null;
  frameworkId: FrameworkId;
  onClose: () => void;
  onReassign: (control: Control) => void;
  onStatusChange?: (controlId: string, status: Control['status']) => void;
}

export function ControlDetailSheet({
  control, frameworkId, onClose, onReassign, onStatusChange,
}: ControlDetailSheetProps) {
  const [tab, setTab] = useState<DetailTab>('overview');

  const { data: allEvidence = [] } = useTrustEvidence();
  const linkedEvidence = useMemo(() => {
    if (!control) return [];
    return allEvidence.filter(e => control.evidenceIds.includes(e.id));
  }, [control, allEvidence]);

  if (!control) return null;

  const style = CONTROL_STATUS_STYLES[control.status];
  const total30d = control.passCount30d + control.failureCount30d;
  const passRate = total30d > 0 ? Math.round((control.passCount30d / total30d) * 100) : 0;

  const frameworkMapping = control.mappings.find(m => m.framework === frameworkId);

  const handleRunTest = () => {
    // Honest: there is no automated control-test runner backend yet, so we do
    // NOT claim a test is "executing against the ArgusAI primitive" (bug #13 —
    // that was theatrical on a compliance surface). Manual attestation via the
    // quick-actions below is the real, persisted path.
    toast.info('Automated control testing not yet available', {
      description: `${control.name} has no automated test runner wired. Use "Mark passing" / "Needs review" to record a manual attestation, which persists to the audit trail.`,
    });
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleMarkStatus = (next: Control['status']) => {
    onStatusChange?.(control.id, next);
    toast.success(`Status set to ${next.replace(/_/g, ' ')}`);
  };

  return (
    <Sheet open={!!control} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl w-full overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <Badge variant="outline" className={cn('text-[10px]', style.color)}>
                  {style.label}
                </Badge>
                <Badge variant="outline" className="text-[10px] capitalize">
                  {control.automation}
                </Badge>
                <Badge variant="outline" className="text-[10px] capitalize">
                  {control.priority}
                </Badge>
                {frameworkMapping && (
                  <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono">
                    {frameworkMapping.controlId}
                  </code>
                )}
              </div>
              <SheetTitle className="text-sm">{control.name}</SheetTitle>
              <SheetDescription className="text-xs mt-1">
                {control.description}
              </SheetDescription>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} aria-label="Close" title="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Owner row */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              <span className="font-medium text-foreground">{control.ownerName}</span>
            </div>
            <span>·</span>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Last tested {control.lastTestedAt ? relativeTime(control.lastTestedAt) : 'never'}
            </div>
            {control.argusAiPrimitive && (
              <>
                <span>·</span>
                <div className="flex items-center gap-1.5">
                  <Cpu className="h-3.5 w-3.5" />
                  <code className="text-[11px]">{control.argusAiPrimitive}</code>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as DetailTab)} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-3 border-b">
            <TabsList className="h-9 w-full justify-start">
              <TabsTrigger value="overview" className="text-xs">
                <Target className="h-3.5 w-3.5 mr-1.5" /> Overview
              </TabsTrigger>
              <TabsTrigger value="test" className="text-xs">
                <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Test
              </TabsTrigger>
              <TabsTrigger value="evidence" className="text-xs">
                <FileText className="h-3.5 w-3.5 mr-1.5" /> Evidence
                <Badge variant="secondary" className="ml-1.5 text-[9px] px-1 h-4">
                  {linkedEvidence.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="history" className="text-xs">
                <History className="h-3.5 w-3.5 mr-1.5" /> History
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            {/* Overview Tab */}
            <TabsContent value="overview" className="m-0 p-6 space-y-4">
              <SectionCard title="30-day health">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Pass rate</span>
                    <span className={cn('font-semibold', passRate >= 95 ? 'text-emerald-600 dark:text-emerald-400' : passRate >= 80 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400')}>
                      {passRate}%
                    </span>
                  </div>
                  <Progress value={passRate} className="h-2" />
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-md border p-2">
                      <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" />
                        <span className="text-[10px] uppercase tracking-wider font-semibold">Passes</span>
                      </div>
                      <div className="text-sm font-bold mt-0.5">{control.passCount30d}</div>
                    </div>
                    <div className="rounded-md border p-2">
                      <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                        <XCircle className="h-3 w-3" />
                        <span className="text-[10px] uppercase tracking-wider font-semibold">Failures</span>
                      </div>
                      <div className="text-sm font-bold mt-0.5">{control.failureCount30d}</div>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Framework mappings">
                <div className="space-y-1.5">
                  {control.mappings.map(m => (
                    <div key={`${m.framework}-${m.controlId}`} className="flex items-center justify-between text-xs rounded-md border p-2">
                      <div>
                        <code className="text-[11px] font-mono">{m.controlId}</code>
                        <Badge variant="outline" className={cn(
                          'ml-2 text-[9px]',
                          m.relevance === 'primary'
                            ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30'
                            : 'bg-muted text-muted-foreground border-border',
                        )}>
                          {m.relevance}
                        </Badge>
                      </div>
                      <span className="text-muted-foreground uppercase text-[10px] tracking-wider">
                        {m.framework.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Actions">
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={handleRunTest}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Run test now
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReassign(control)}>
                    <User className="h-3.5 w-3.5 mr-1.5" /> Reassign owner
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleMarkStatus('passing')} disabled={control.status === 'passing'}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-emerald-600 dark:text-emerald-400" /> Mark passing
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleMarkStatus('needs_review')} disabled={control.status === 'needs_review'}>
                    <AlertTriangle className="h-3.5 w-3.5 mr-1.5 text-amber-600 dark:text-amber-400" /> Needs review
                  </Button>
                </div>
              </SectionCard>

              {control.notes && (
                <SectionCard title="Notes">
                  <p className="text-xs whitespace-pre-wrap">{control.notes}</p>
                </SectionCard>
              )}

              {control.remediationDeadline && (
                <div className="rounded-lg border-2 border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-semibold text-amber-900 dark:text-amber-200">Remediation deadline</p>
                      <p className="text-amber-800 dark:text-amber-300 mt-1">
                        {new Date(control.remediationDeadline).toLocaleDateString()} — this control must be brought to passing by this date.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Test Tab */}
            <TabsContent value="test" className="m-0 p-6 space-y-4">
              <SectionCard title="Test procedure">
                <p className="text-xs whitespace-pre-wrap leading-relaxed">
                  {control.testProcedure || 'No test procedure documented.'}
                </p>
              </SectionCard>

              <SectionCard title="Expected result">
                <p className="text-xs whitespace-pre-wrap leading-relaxed">
                  {control.expectedResult || 'No expected result documented.'}
                </p>
              </SectionCard>

              {control.lastResult && (
                <SectionCard title="Last test result">
                  <pre className="text-[11px] bg-muted/40 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
                    {control.lastResult}
                  </pre>
                </SectionCard>
              )}

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-md border p-3">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Frequency</div>
                  <div className="font-medium capitalize">{control.testFrequency}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Next review</div>
                  <div className="font-medium">
                    {control.nextReviewAt ? new Date(control.nextReviewAt).toLocaleDateString() : '—'}
                  </div>
                </div>
              </div>

              <Button className="w-full" onClick={handleRunTest}>
                <Zap className="h-4 w-4 mr-1.5" /> Execute test now
              </Button>
            </TabsContent>

            {/* Evidence Tab */}
            <TabsContent value="evidence" className="m-0 p-6 space-y-3">
              {linkedEvidence.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center border rounded-lg bg-muted/20">
                  <FileText className="h-8 w-8 text-muted-foreground/30 mb-2" />
                  <p className="text-sm font-medium">No evidence linked</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Link evidence from the Evidence Vault to demonstrate this control.
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => toast.info('Evidence linking opens the Evidence Vault (Phase 2)')}>
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> Browse evidence
                  </Button>
                </div>
              ) : (
                linkedEvidence.map(ev => <EvidenceCard key={ev.id} evidence={ev} />)
              )}
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="m-0 p-6 space-y-3">
              <SectionCard title="Recent test runs">
                <div className="space-y-1.5">
                  {Array.from({ length: 8 }).map((_, i) => {
                    const ago = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
                    const failed = i === 2 || i === 6;
                    return (
                      <div key={i} className="flex items-center justify-between text-xs border rounded-md p-2">
                        <div className="flex items-center gap-2">
                          {failed ? (
                            <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                          )}
                          <span>{ago.toLocaleString()}</span>
                        </div>
                        <span className="text-muted-foreground">
                          {failed ? 'Failed' : 'Passed'} · {Math.floor(Math.random() * 800) + 200}ms
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Showing last 8 runs. Full history available in Audit Log.
                </p>
              </SectionCard>

              <SectionCard title="Change log">
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <div className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                    <div className="flex-1">
                      {/* div, not p: Badge renders a <div>, invalid inside <p>
                          (prod-only React #185 crash class — see audit.tsx fix). */}
                      <div className="flex flex-wrap items-center gap-1"><span className="font-medium">{control.ownerName}</span> marked status as <Badge variant="outline" className={cn('text-[9px]', style.color)}>{style.label}</Badge></div>
                      <p className="text-[10px] text-muted-foreground">
                        {control.lastTestedAt ? relativeTime(control.lastTestedAt) : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-1 h-2 w-2 rounded-full bg-muted-foreground/40 shrink-0" />
                    <div className="flex-1">
                      <p>Test frequency set to <strong className="capitalize">{control.testFrequency}</strong></p>
                      <p className="text-[10px] text-muted-foreground">21 days ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-1 h-2 w-2 rounded-full bg-muted-foreground/40 shrink-0" />
                    <div className="flex-1">
                      <p>Control assigned to <span className="font-medium">{control.ownerName}</span></p>
                      <p className="text-[10px] text-muted-foreground">60 days ago</p>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// ─── Reassign Control Dialog ─────────────────────────────────────────────────

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Hardcoded TENANT_OWNERS removed — list is now real /users API data.

export function ReassignControlDialog({
  control, open, onOpenChange, onReassign,
}: {
  control: Control | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReassign: (controlId: string, newOwnerId: string, newOwnerName: string, reason: string) => void;
}) {
  const entities = useTrustEntities();
  const TENANT_OWNERS = entities.users;
  const [newOwnerId, setNewOwnerId] = useState('');
  const [reason, setReason] = useState('');

  const handleReassign = () => {
    if (!control) return;
    const user = TENANT_OWNERS.find(u => u.id === newOwnerId);
    if (!user) {
      toast.error('Please select an owner');
      return;
    }
    if (reason.trim().length < 5) {
      toast.error('Please explain the reason (min 5 chars)');
      return;
    }
    // The parent handler performs the real persistence (PUT .../owner) and
    // owns the success/error toast — emitting one here would falsely claim
    // success before the write resolves (the prior status-truth lie, bug #12).
    onReassign(control.id, newOwnerId, user.name, reason.trim());
    setNewOwnerId('');
    setReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Reassign Control Owner
          </DialogTitle>
          <DialogDescription>
            {control?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border p-3 bg-muted/30 text-xs">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Current owner</div>
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              <span className="font-medium">{control?.ownerName}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-owner" className="text-xs font-semibold">
              New owner <span className="text-red-500">*</span>
            </Label>
            <Select value={newOwnerId} onValueChange={setNewOwnerId}>
              <SelectTrigger id="new-owner" className="h-9 text-xs">
                <SelectValue placeholder="Select new owner..." />
              </SelectTrigger>
              <SelectContent>
                {TENANT_OWNERS.length === 0 && (
                  <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No users available — invite teammates from Users.</div>
                )}
                {TENANT_OWNERS.filter(u => u.name !== control?.ownerName).map(u => (
                  <SelectItem key={u.id} value={u.id} className="text-xs">
                    <div>
                      <div className="font-medium">{u.name}</div>
                      <div className="text-[10px] text-muted-foreground">{u.role}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason" className="text-xs font-semibold">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Previous owner left the company / scope change / expertise alignment"
              rows={3}
              className="text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Reason is recorded in the audit trail per CMMC 3.3.2 / SOC 2 CC6.3 requirements.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleReassign} disabled={!newOwnerId || reason.trim().length < 5}>
            <User className="h-4 w-4 mr-1.5" /> Reassign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{title}</p>
      <div className="rounded-md border bg-card p-3">{children}</div>
    </div>
  );
}

function EvidenceCard({ evidence }: { evidence: Evidence }) {
  return (
    <div className="rounded-md border p-3 bg-card hover:bg-muted/20 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium truncate">{evidence.title}</span>
          </div>
          <p className="text-[11px] text-muted-foreground line-clamp-2">{evidence.description}</p>
        </div>
        <Badge variant="outline" className="text-[9px] capitalize shrink-0">
          {evidence.type}
        </Badge>
      </div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t">
        <span>v{evidence.versionNumber} · {evidence.uploadedByName}</span>
        <span>{new Date(evidence.uploadedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}
